import {
	useState,
	forwardRef,
	useImperativeHandle,
	useRef,
	useEffect,
	useMemo,
	useCallback
} from 'react';
import { TextField, FormHelperText, Tooltip, FormControl } from '@mui/material';
import SignatureCanvas from 'react-signature-canvas';

import ComponentHeader from '../ComponentHeader';
import { getElementIcon, stripTextHTML } from '../../../FormBuilder/utils';

import StyledWrapper from '../style';

const getSanitizedError = (errorValue) => {
	if (typeof errorValue === 'object' && errorValue !== null) {
		return errorValue;
	}
	return errorValue === true;
};

const Signature = forwardRef(
	(
		{ defaultValue = '', hasError, editModeOn, read_only, data, index, mutable, _onDestroy },
		ref
	) => {
		const initialError = useMemo(() => getSanitizedError(hasError), [hasError]);
		const [canvasDataURL, setCanvasDataURL] = useState(defaultValue);
		const [error, setError] = useState(initialError);
		const [isLabelOverflowing, setIsLabelOverflowing] = useState(false);

		const labelRef = useRef(null);
		const signatureCanvasRef = useRef(null);
		const hasLoadedRef = useRef(false);

		useImperativeHandle(ref, () => ({ getCanvasDataURL: () => canvasDataURL }));

		const drawDataURL = useCallback((dataURL) => {
			if (!signatureCanvasRef.current || !dataURL) return;
			const canvas = signatureCanvasRef.current.getCanvas();
			const ctx = canvas.getContext('2d');
			ctx.clearRect(0, 0, canvas.width, canvas.height);
			signatureCanvasRef.current.fromDataURL(dataURL, {
				width: canvas.width,
				height: canvas.height,
				ratio: 1
			});
		}, []);

		useEffect(() => {
			if (!hasLoadedRef.current && !read_only && defaultValue) {
				const timeoutId = setTimeout(() => {
					drawDataURL(defaultValue);
					hasLoadedRef.current = true;
				}, 100);
				return () => clearTimeout(timeoutId);
			}
		}, [read_only, defaultValue, drawDataURL]);

		useEffect(() => {
			if (!canvasDataURL) return;
			const canvasEl = signatureCanvasRef.current?.getCanvas();
			const wrapper = canvasEl?.parentElement;
			if (!wrapper || typeof ResizeObserver === 'undefined') return;
			const ro = new ResizeObserver(() => drawDataURL(canvasDataURL));
			ro.observe(wrapper);
			return () => ro.disconnect();
		}, [canvasDataURL, drawDataURL]);

		const triggerFormBlur = () => {
			setTimeout(() => {
				document.getElementById('hiddenBlurTriggerInput')?.click();
			}, 300);
		};

		const clearData = () => {
			if (signatureCanvasRef.current) signatureCanvasRef.current.clear();
			setCanvasDataURL('');
			setError(false);
			triggerFormBlur();
		};

		const saveData = () => {
			if (signatureCanvasRef.current && !signatureCanvasRef.current.isEmpty()) {
				const canvas = signatureCanvasRef.current.getCanvas();
				const dataUrl = canvas.toDataURL();
				setCanvasDataURL(dataUrl);
				setError(false);
			}
			triggerFormBlur();
		};

		const isFormBuilder = editModeOn || index >= 0;
		const isSingleElement = index > 0;
		const rawLabelText = stripTextHTML(data?.label);
		const cleanLabelText = rawLabelText.replace(/\*+\s*$/, '').trim();
		const fullLabelText = `${cleanLabelText}${
			isFormBuilder
				? data?.unique_identifier
					? ` (ff-${data?.unique_identifier})`
					: ` (tmp-${data?.temp_order || index})`
				: ''
		}`;
		const labelFontSize = !isFormBuilder || isSingleElement ? '22px' : undefined; // Use undefined to skip adding a style when not enlarged.

		useEffect(() => {
			const measureOverflow = () => {
				const el = labelRef.current;
				if (!el) return;
				const isOverflow = el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;
				setIsLabelOverflowing(isOverflow);
			};
			let rafId = requestAnimationFrame(measureOverflow);
			const timeoutId = setTimeout(() => {
				rafId = requestAnimationFrame(measureOverflow);
			}, 250);
			let ro;
			if (typeof ResizeObserver !== 'undefined' && labelRef.current) {
				ro = new ResizeObserver(() => {
					cancelAnimationFrame(rafId);
					rafId = requestAnimationFrame(measureOverflow);
				});
				ro.observe(labelRef.current);
			}
			const onWindowResize = () => {
				cancelAnimationFrame(rafId);
				rafId = requestAnimationFrame(measureOverflow);
			};
			window.addEventListener('resize', onWindowResize);
			return () => {
				window.removeEventListener('resize', onWindowResize);
				if (ro) ro.disconnect();
				cancelAnimationFrame(rafId);
				clearTimeout(timeoutId);
			};
		}, [fullLabelText]);

		const currentError = initialError || error;

		const errorMessage = (() => {
			if (!currentError) return null;
			if (typeof currentError === 'object' && currentError.error)
				return `${cleanLabelText} ${currentError.error}`;
			if (currentError === true) return `${cleanLabelText} is a required field.`;
			return null;
		})();
		const helperId = useMemo(() => `${data?.field_name || 'signature'}-helper`, [data?.field_name]);

		return (
			<div className="SortableItem rfb-item alwaysbreak">
				<ComponentHeader
					data={data}
					index={index}
					editModeOn={editModeOn}
					mutable={mutable}
					_onDestroy={_onDestroy}
				/>
				<div className="inner-form-group">
					<FormControl sx={{ width: '100%' }} variant="outlined">
						<StyledWrapper.CustomLabel
							disabled={read_only}
							sx={labelFontSize ? { fontSize: labelFontSize } : {}}
						>
							<StyledWrapper.SignatureLabelRow>
								{isFormBuilder && (
									<StyledWrapper.SignatureIconWrapper>
										{getElementIcon(data?.key)}
									</StyledWrapper.SignatureIconWrapper>
								)}
								<Tooltip arrow placement="top" title={isLabelOverflowing ? fullLabelText : ''}>
									<StyledWrapper.SignatureLabelText ref={labelRef}>
										{cleanLabelText}
										{isFormBuilder && (
											<span>
												{data?.unique_identifier
													? ` (ff-${data?.unique_identifier})`
													: ` (tmp-${data?.temp_order || index})`}
											</span>
										)}
									</StyledWrapper.SignatureLabelText>
								</Tooltip>
								{data?.required && (
									<StyledWrapper.SignatureAsterisk>*</StyledWrapper.SignatureAsterisk>
								)}
							</StyledWrapper.SignatureLabelRow>
						</StyledWrapper.CustomLabel>
						{isFormBuilder ? (
							<TextField disabled multiline rows={4} fullWidth InputLabelProps={{ shrink: true }} />
						) : (
							<StyledWrapper.BorderOutline
								error={!!errorMessage}
								src={data.src}
								disabled={read_only}
							>
								<StyledWrapper.SignaturePadContainer
									className="form-group signature-form-group"
									name={data.field_name}
									aria-describedby={data?.help_message ? helperId : undefined}
								>
									{read_only ? (
										canvasDataURL ? (
											<StyledWrapper.SignatureReadOnlyImage src={canvasDataURL} alt="Signature" />
										) : (
											<TextField
												disabled
												fullWidth
												variant="standard"
												InputProps={{
													disableUnderline: false,
													style: { height: '150px', textAlign: 'center' }
												}}
											/>
										)
									) : (
										<>
											<SignatureCanvas ref={signatureCanvasRef} onEnd={saveData} canvasProps={{}} />
											<StyledWrapper.SignatureClearIcon
												className="fas fa-times"
												onClick={clearData}
												title="Clear Signature"
											/>
										</>
									)}
									<StyledWrapper.HiddenInput
										type="hidden"
										name={data.field_name}
										value={canvasDataURL}
									/>
								</StyledWrapper.SignaturePadContainer>
							</StyledWrapper.BorderOutline>
						)}
					</FormControl>
				</div>
				{(errorMessage || data?.help_message) && (
					<StyledWrapper.FormHelperTextContainer>
						{errorMessage && <FormHelperText error>{errorMessage}</FormHelperText>}
						{data?.help_message && (
							<FormHelperText id={helperId}>{data.help_message}</FormHelperText>
						)}
					</StyledWrapper.FormHelperTextContainer>
				)}
			</div>
		);
	}
);

Signature.displayName = 'Signature';
export default Signature;
